import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BadgePercent, BellRing, ChevronRight, Star, Users, Wifi } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useAppDispatch, useAppSelector } from '@/Redux/store';
import { leaveSession } from '@/Redux/Slices/guestSlice';

/** Everything a guest needs occasionally, one thumb-reach from the tab bar. */
export default function More() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useAppSelector((s) => s.ui.theme);

  const links = [
    // Everyone at the table sees this: the visit belongs to the table, not to
    // one guest who was made its host.
    { to: '/visit', key: 'guest.more.visit', icon: Users },
    { to: '/service', key: 'guest.more.service', icon: BellRing },
    { to: '/offers', key: 'guest.more.offers', icon: BadgePercent },
    { to: '/wifi', key: 'guest.more.wifi', icon: Wifi },
    { to: '/feedback', key: 'guest.more.feedback', icon: Star },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="divide-y p-0">
          {links.map(({ to, key, icon: Icon }) => (
            <Link key={to} to={to} className="flex items-center gap-3 p-4">
              <Icon className="h-5 w-5 shrink-0 text-accent" />
              <span className="flex-1 font-medium">{t(key)}</span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground rtl:rotate-180" />
            </Link>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="divide-y p-0">
          <div className="flex items-center justify-between gap-3 p-4">
            <span className="text-sm font-medium">{t('guest.more.language')}</span>
            <LanguageSwitcher />
          </div>

          <div className="flex items-center justify-between gap-3 p-4">
            <span className="min-w-0">
              <span className="block text-sm font-medium">{t('guest.theme.label')}</span>
              {/* Only says anything while `Auto` is selected — otherwise it
                  would be describing a rule the guest has already overridden. */}
              {theme === 'system' && (
                <span className="block text-xs text-muted-foreground">
                  {t('guest.theme.systemHint')}
                </span>
              )}
            </span>
            <ThemeToggle />
          </div>
        </CardContent>
      </Card>

      <Button variant="outline" className="w-full" onClick={() => dispatch(leaveSession())}>
        {t('guest.more.leave')}
      </Button>
    </div>
  );
}
